import { apiConfig } from '@unreserved/configs';
import {
  Configuration,
  AuthenticationApi,
  UserApi,
  ListingApi,
  OfferApi,
  DashboardApi,
  NotificationsApi,
  AdminNotificationsApi,
  type Middleware,
  EnquiryApi,
  StaticApi,
  InspectionApi,
} from '@unreserved/sdk';
import { ADMIN_ROUTES } from '@/app/_libs/constants/routes';
import { DEVICE_ID_KEY } from '@unreserved/constants/notifications';

type FetchApi = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

/**
 * HTTP Status codes
 */
const HttpStatus = {
  UNAUTHORIZED: 401,
} as const;
const refreshTokenMiddleware: Middleware = {
  async post({ fetch: wrappedFetch, url, init, response }) {
    if (response.status !== HttpStatus.UNAUTHORIZED) {
      return;
    }

    const headers = new Headers(init.headers as HeadersInit | undefined);

    // Prevent infinite retry loops
    if (headers.get('x-refresh-retry') === 'true') {
      return;
    }

    // Attempt to refresh token using cookies
    const refreshResponse = await fetch(
      `${apiConfig.baseUrl}/v1/auth/refresh-token`,
      {
        method: 'POST',
        headers: {
          ...apiConfig.defaultHeaders,
          'x-landing-app-id': 'admin',
        },
        credentials: apiConfig.withCredentials ? 'include' : 'omit',
      },
    );

    // If refresh fails, logout with deviceId and redirect to login
    if (!refreshResponse.ok) {
      if (typeof window !== 'undefined') {
        const deviceId = localStorage.getItem(DEVICE_ID_KEY) ?? '';
        try {
          await fetch(`${apiConfig.baseUrl}/v1/auth/logout`, {
            method: 'POST',
            headers: {
              ...apiConfig.defaultHeaders,
              'Content-Type': 'application/json',
              'x-landing-app-id': 'admin',
            },
            credentials: apiConfig.withCredentials ? 'include' : 'omit',
            body: JSON.stringify({ deviceId }),
          });
        } catch (error) {
          console.error('Logout failed:', error);
        }
        localStorage.removeItem(DEVICE_ID_KEY);
        window.location.href = ADMIN_ROUTES.LOGIN;
      }
      return;
    }

    // Retry the original request with refresh flag
    headers.set('x-refresh-retry', 'true');

    return wrappedFetch(url, {
      ...init,
      headers,
    });
  },
};

const fetchApi: FetchApi = (input, init) => {
  const headers = new Headers(init?.headers);
  headers.set('x-landing-app-id', 'admin');

  if (apiConfig.defaultHeaders) {
    Object.entries(apiConfig.defaultHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
  }

  return fetch(input, {
    ...init,
    credentials: apiConfig.withCredentials ? 'include' : 'omit',
    headers,
  });
};
const adminSdkConfiguration = new Configuration({
  basePath: apiConfig.baseUrl,
  fetchApi,
  credentials: apiConfig.withCredentials ? 'include' : 'omit',
  headers: {
    ...apiConfig.defaultHeaders,
    'x-landing-app-id': 'admin',
  },
  middleware: [refreshTokenMiddleware],
});

export const adminAuthApi = new AuthenticationApi(adminSdkConfiguration);
export const adminCustomersApi = new UserApi(adminSdkConfiguration);
export const adminListingsApi = new ListingApi(adminSdkConfiguration);
export const adminOffersApi = new OfferApi(adminSdkConfiguration);
export const adminEnquiriesApi = new EnquiryApi(adminSdkConfiguration);
export const adminStaticApi = new StaticApi(adminSdkConfiguration);
export const adminDashboardApi = new DashboardApi(adminSdkConfiguration);
export const adminInspectionsApi = new InspectionApi(adminSdkConfiguration);
export const adminNotificationsApi = new NotificationsApi(
  adminSdkConfiguration,
);
export const adminBroadcastApi = new AdminNotificationsApi(adminSdkConfiguration);

export function getAdminAuthApi(): AuthenticationApi {
  return adminAuthApi;
}
export function getAdminCustomersApi(): UserApi {
  return adminCustomersApi;
}
export function getAdminListingsApi(): ListingApi {
  return adminListingsApi;
}
export function getAdminOffersApi(): OfferApi {
  return adminOffersApi;
}
export function getAdminEnquiriesApi(): EnquiryApi {
  return adminEnquiriesApi;
}
export function getAdminStaticDataApi(): StaticApi {
  return adminStaticApi;
}

export function getAdminDashboardApi(): DashboardApi {
  return adminDashboardApi;
}

export function getAdminInspectionsApi(): InspectionApi {
  return adminInspectionsApi;
}

export function getAdminNotificationsApi(): NotificationsApi {
  return adminNotificationsApi;
}

export function getAdminBroadcastApi(): AdminNotificationsApi {
  return adminBroadcastApi;
}
